/** @format */
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { FudisSelectOption } from '@funidata/ngx-fudis/lib/types/forms';
import { TranslocoService } from '@ngneat/transloco';
import { CourseUnitResultItem, OtmId } from 'common-typescript/types';
import { BehaviorSubject, mergeAll, Observable, of, Subject, take, toArray } from 'rxjs';
import { filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { LocaleService } from 'sis-common/l10n/locale.service';
import { AppErrorHandler } from '../../error-handler/app-error-handler';
import { CreditRangePipe } from '../../number/credit-range.pipe';
import { OrganisationNameService } from '../../organisation/organisation-name.service';
import { CourseUnitEntityService, CourseUnitSearchQueryParams } from '../../service/course-unit-entity.service';

export interface CourseUnitSearchOptionType {
    value: CourseUnitResultItem;
    label: string;
    universityOrgIds: OtmId[];
    isRelatedToCurrentUniversity: boolean;
    subLabel?: string;
}

export interface CourseUnitSearchGroup {
    homeUniversityCourseUnits: FudisSelectOption<CourseUnitSearchOptionType>[];
    cooperationNetworkCourseUnits: FudisSelectOption<CourseUnitSearchOptionType>[];
}

@Component({
    selector: 'sis-course-unit-search-select',
    templateUrl: './course-unit-search-select.component.html',
    encapsulation: ViewEncapsulation.None,
})
export class CourseUnitSearchSelectComponent {
    @Input({ required: true }) currentUniversityOrgId: OtmId;
    @Input({ required: true }) crossStudyFeaturesEnabled: boolean;
    @Input({ required: true }) cooperationNetworkIds: OtmId[];
    @Input({ required: true }) curriculumPeriodIds: OtmId[];
    @Input() excludedCourseUnitIds: Set<OtmId> = new Set();

    @Output() updateSelected = new EventEmitter<CourseUnitResultItem>();

    uiLang: string;

    courseUnitSelectControl = new FormControl<null | FudisSelectOption<CourseUnitResultItem>>(null);

    protected searchTextUpdateSubject = new Subject<string | null>();
    protected searchResults = new BehaviorSubject<CourseUnitSearchGroup>({
        homeUniversityCourseUnits: [],
        cooperationNetworkCourseUnits: [],
    });

    constructor(
        private readonly appErrorHandler: AppErrorHandler,
        private readonly courseUnitEntityService: CourseUnitEntityService,
        private readonly creditRangePipe: CreditRangePipe,
        private readonly translocoService: TranslocoService,
        private readonly organisationNameService: OrganisationNameService,
        private readonly localeService: LocaleService,
    ) {
        this.searchTextUpdateSubject
            .pipe(
                tap(() =>
                    this.searchResults?.next({
                        homeUniversityCourseUnits: [],
                        cooperationNetworkCourseUnits: [],
                    }),
                ),
                filter(textQuery => textQuery?.trim() !== ''),
                switchMap(textQuery => this.searchCourseUnits(textQuery)),
                takeUntilDestroyed(),
            )
            .subscribe();

        this.uiLang = this.localeService.getCurrentLanguage();
    }

    searchCourseUnits(textQuery: string): Observable<CourseUnitSearchOptionType[]> {
        const searchParameters = {
            fullTextQuery: textQuery,
            uiLang: this.uiLang,
            orgRootId: this.currentUniversityOrgId,
            curriculumPeriodId: this.curriculumPeriodIds,
            validity: 'ONGOING_AND_FUTURE',
            limit: 100,
            start: 0,
        };

        const cooperationNetworkParameters = this.crossStudyFeaturesEnabled
            ? {
                  cooperationNetworkId: this.cooperationNetworkIds,
                  cooperationNetworkSearchType: 'COURSE_UNITS_COMBINED_WITH_COOPERATION_NETWORKS',
                  validityInCooperationNetwork: 'ONGOING',
              }
            : {};

        const queryParams = Object.assign(searchParameters, cooperationNetworkParameters) as CourseUnitSearchQueryParams;

        return this.courseUnitEntityService.searchActive(queryParams).pipe(
            map(searchResult => searchResult.searchResults),
            mergeAll(),
            filter((courseUnitResultItem: CourseUnitResultItem) => !this.excludedCourseUnitIds.has(courseUnitResultItem.id)),
            map((courseUnitResultItem: CourseUnitResultItem) => this.courseUnitResultItemToOption(courseUnitResultItem)),
            mergeMap((selectOption: FudisSelectOption<CourseUnitSearchOptionType>) => {
                if (selectOption.isRelatedToCurrentUniversity) {
                    return of(selectOption);
                }
                return this.organisationNameService.getOrgName(selectOption.universityOrgIds[0]).pipe(
                    take(1),
                    map(universityName => ({
                        ...selectOption,
                        label: selectOption.label.concat(` | ${universityName}`),
                    })),
                );
            }),
            toArray(),
            tap((selectOptions: FudisSelectOption<CourseUnitSearchOptionType>[]) => {
                this.searchResults?.next(
                    this.crossStudyFeaturesEnabled
                        ? {
                              homeUniversityCourseUnits: selectOptions.filter(option => option.isRelatedToCurrentUniversity),
                              cooperationNetworkCourseUnits: selectOptions.filter(option => !option.isRelatedToCurrentUniversity),
                          }
                        : {
                              homeUniversityCourseUnits: [...selectOptions],
                              cooperationNetworkCourseUnits: [],
                          },
                );
            }),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }

    private courseUnitResultItemToOption(courseUnitResultItem: CourseUnitResultItem): CourseUnitSearchOptionType {
        return {
            value: courseUnitResultItem,
            label: `${courseUnitResultItem.name} (${this.creditRangePipe.transform(courseUnitResultItem.credits)})`,
            subLabel: `${courseUnitResultItem.code} | ${this.translocoService.translate('SIS_COMPONENTS.STUDY.COURSE_UNIT')}`,
            universityOrgIds: courseUnitResultItem.universityOrgIds,
            isRelatedToCurrentUniversity: courseUnitResultItem.universityOrgIds.includes(this.currentUniversityOrgId),
        };
    }
}
